import type { FC, ReactNode} from 'react';
import { createElement, lazy, useState } from 'react';
import type { Callable, UserComponent } from 'react-call';
import { createCallable } from 'react-call';
import { appendIfMissing } from './useArrayState';
import useComponentDidMount from './useComponentDidMount';

const initialRoots: Set<FC<unknown>> = new Set();
let addRoot: (root: FC<unknown>) => void = (root) => initialRoots.add(root);

const { promise: rootMounted, resolve: setRootMounted } = Promise.withResolvers<void>();

export function LazyCallableRoot() {
    const [roots, setRoots] = useState(Array.from(initialRoots));

    useComponentDidMount(() => {
        addRoot = (root) => setRoots(appendIfMissing(root));
        setRootMounted();
    });

    return (
        <>
            {roots.map((R, i) => (
                <R key={i} />
            ))}
        </>
    );
}

export default function createLazyCallable<Props = void, Response = void>(
    moduleImport: () => Promise<{ default: UserComponent<Props, Response, unknown> }>,
    unmountingDelay?: number,
    wrapper?: FC<{ children: ReactNode }>,
): Pick<Callable<Props, Response, unknown>, 'call' | 'upsert' | 'end' | 'update'> {
    return createAutomountingCallable(lazy(moduleImport), unmountingDelay, wrapper);
}

export function createAutomountingCallable<Props = void, Response = void>(
    component: UserComponent<Props, Response, unknown>,
    unmountingDelay?: number,
    wrapper?: FC<{ children: ReactNode }>,
): Pick<Callable<Props, Response, unknown>, 'call' | 'upsert' | 'end' | 'update'> {
    const callable = createCallable(component, unmountingDelay);

    if (wrapper) {
        addRoot(() => createElement(wrapper, null, createElement(callable)));
    } else {
        addRoot(callable);
    }

    return {
        async call(...args: Parameters<typeof callable.call>) {
            await rootMounted;
            return callable.call(...args);
        },
        async upsert(...args: Parameters<typeof callable.upsert>) {
            await rootMounted;
            return callable.upsert(...args);
        },
        end: callable.end,
        update: callable.update,
    };
}
